import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '../auth';
import { paymentAPI, type PaymentRate, type PaymentStats, type PaymentItem } from '@/api/payment';
import { handleApiError } from '@/lib/api';

const paymentSchema = z.object({
  amount_usd: z.number().min(10, { message: 'Минимальная сумма 10 USD' }).max(10000, { message: 'Максимальная сумма 10000 USD' }),
});

const statsSchema = z.object({
  total_payments: z.number(),
  completed_payments: z.number(),
  pending_payments: z.number(),
  expired_payments: z.number(),
  total_spent_usd: z.number(),
  total_requests_purchased: z.number(),
  last_successful_payment: z.string().nullable(),
});

// Предустановленные суммы для выбора
const presetAmounts = [
  { value: "10", label: "$10" },
  { value: "20", label: "$20" },
  { value: "50", label: "$50" },
  { value: "200", label: "$200" },
  { value: "500", label: "$500" },
  { value: "custom", label: "Другая сумма" },
];

export function Payment() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [paymentRate, setPaymentRate] = useState<PaymentRate | null>(null);
  const [myPayments, setMyPayments] = useState<PaymentItem[]>([]);
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentItem | null>(null);
  const [paymentStats, setPaymentStats] = useState<PaymentStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<string>("");
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);

  const form = useForm<z.infer<typeof paymentSchema>>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      amount_usd: 10,
    },
  });

  useEffect(() => {
    if (user) {
      fetchPaymentData();
    } else {
      setLoading(false);
      setError('Необходимо войти в систему для доступа к платежам');
    }
  }, [user]);

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      
      // Получаем курс и настройки
      try {
        const rateData = await paymentAPI.getPaymentRate();
        if (rateData.ok && rateData.data) {
          setPaymentRate(rateData.data);
        }
      } catch (err) {
        console.warn('Failed to load payment rate:', err);
      }

      // Получаем мои платежи
      try {
        const paymentsData = await paymentAPI.getMyPayments();
        if (paymentsData.ok) {
          setMyPayments(paymentsData.data || []);
        }
      } catch (err) {
        console.warn('Failed to load payments:', err);
      }

    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentDetails = async (payment: PaymentItem) => {
    setStatsLoading(true);
    try {
      const detailsData = await paymentAPI.getPaymentDetails(payment.id);
      if (detailsData.ok) {
        setSelectedPayment({ ...payment, ...detailsData.data });
      } else {
        // Если не удалось получить детали, используем базовую информацию
        setSelectedPayment(payment);
      }
    } catch (err) {
      // В случае ошибки показываем базовую информацию о платеже
      setSelectedPayment(payment);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleAmountSelect = (value: string) => {
    setSelectedAmount(value);
    setComboboxOpen(false);
    
    if (value === "custom") {
      setShowCustomInput(true);
      form.setValue('amount_usd', 10); // Устанавливаем минимальное значение
    } else {
      setShowCustomInput(false);
      const amount = parseFloat(value);
      form.setValue('amount_usd', amount);
    }
  };

  const onSubmit = async (values: z.infer<typeof paymentSchema>) => {
    setCreatingPayment(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await paymentAPI.createPayment(values.amount_usd);
      
      if (!data.ok) {
        throw new Error(data.error || 'Ошибка создания платежа');
      }

      setSuccess(`Платеж создан! ID: ${data.data?.payment_id}`);
      form.reset();
      
      // Обновляем список платежей
      await fetchPaymentData();

    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setCreatingPayment(false);
    }
  };

  const calculateRequests = (usdAmount: number) => {
    if (!paymentRate) return 0;
    let requests = usdAmount * paymentRate.rate;
    if (usdAmount >= paymentRate.bonus_threshold) {
      requests *= paymentRate.bonus_multiplier;
    }
    return Math.floor(requests);
  };

  // Получаем платежи для отображения (10 или все)
  const displayedPayments = showAllPayments ? myPayments : myPayments.slice(0, 10);
  const hasMorePayments = myPayments.length > 10;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
      case 'expired':
        return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Завершен';
      case 'pending':
        return 'Ожидает оплаты';
      case 'expired':
        return 'Истек';
      case 'cancelled':
        return 'Отменен';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-7xl">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Пополнение баланса</h1>
            <p className="text-muted-foreground mt-2">
              Загрузка данных о платежах...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-7xl">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">Пополнение баланса</h1>
            <p className="text-muted-foreground mt-2">
              Ошибка: {error}
            </p>
          </div>
          <Button onClick={fetchPaymentData}>Попробовать снова</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl">
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Пополнение баланса</h1>
        </div>

        {/* Форма создания платежа */}
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>Создать платеж</CardTitle>
            <CardDescription>
              Укажите сумму в USD для пополнения баланса
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                  <FormField
                    control={form.control}
                    name="amount_usd"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Сумма (USD)</FormLabel>
                        <FormControl>
                          <div className="space-y-2">
                            <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={comboboxOpen}
                                  className="w-full justify-between"
                                >
                                  {selectedAmount
                                    ? presetAmounts.find((amount) => amount.value === selectedAmount)?.label
                                    : "Выберите сумму..."}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-full p-0">
                                <Command>
                                  <CommandInput placeholder="Поиск суммы..." />
                                  <CommandList>
                                    <CommandEmpty>Сумма не найдена.</CommandEmpty>
                                    <CommandGroup>
                                      {presetAmounts.map((amount) => (
                                        <CommandItem
                                          key={amount.value}
                                          value={amount.value}
                                          onSelect={handleAmountSelect}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              selectedAmount === amount.value ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          {amount.label}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            
                            {showCustomInput && (
                              <Input
                                type="number"
                                min="10"
                                max="10000"
                                step="5"
                                placeholder="Введите сумму в USD"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                {paymentRate && (
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div>Вы получите: {calculateRequests(form.watch('amount_usd'))} запросов</div>
                    {form.watch('amount_usd') >= paymentRate.bonus_threshold && (
                      <div className="text-green-600 font-medium">
                        Бонус x{paymentRate.bonus_multiplier}! При пополнении от {paymentRate.bonus_threshold} USD
                      </div>
                    )}
                  </div>
                )}

                {error && <p className="text-red-500 text-sm">{error}</p>}
                {success && <p className="text-green-500 text-sm">{success}</p>}

                <Button type="submit" className="w-full" disabled={creatingPayment}>
                  {creatingPayment ? 'Создание...' : 'Создать платеж'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* История платежей */}
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle>История платежей</CardTitle>
            <CardDescription>
              Ваши последние платежи и их статус
            </CardDescription>
          </CardHeader>
          <CardContent>
            {myPayments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                У вас пока нет платежей
              </div>
            ) : (
              <div className="space-y-4">
                {displayedPayments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">${payment.amount_usd}</span>
                        <span className={`text-xs px-2 py-1 rounded ${getStatusColor(payment.status)}`}>
                          {getStatusText(payment.status)}
                        </span>
                        {payment.bonus_applied && (
                          <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                            Бонус
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {payment.created_moscow_str || new Date(payment.created_at).toLocaleString()}
                        {payment.used_requests && (
                          <span> • Получено: {payment.used_requests} запросов</span>
                        )}
                      </div>
                      {payment.status === 'pending' && payment.expires_at && (
                        <div className="text-xs text-yellow-600">
                          Истекает: {payment.expires_moscow_str || new Date(payment.expires_at).toLocaleString()} МСК
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right">
                        <div className="text-sm font-medium">ID: {payment.id.slice(0, 8)}...</div>
                        {payment.transaction_hash && (
                          <div className="text-xs text-muted-foreground">
                            TX: {payment.transaction_hash.slice(0, 8)}...
                          </div>
                        )}
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => fetchPaymentDetails(payment)}
                            disabled={statsLoading}
                          >
                            {statsLoading ? 'Загрузка...' : 'Показать'}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>Информация о платеже</DialogTitle>
                            <DialogDescription>
                              Подробная информация о выбранном платеже
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            {selectedPayment ? (
                              <div className="space-y-3">
                                <div className="flex justify-between">
                                  <span className="font-medium">ID платежа:</span>
                                  <span className="text-sm font-mono">{selectedPayment.id}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="font-medium">Сумма:</span>
                                  <span>${selectedPayment.amount_usd}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="font-medium">Статус:</span>
                                  <span className={`px-2 py-1 rounded text-xs ${getStatusColor(selectedPayment.status)}`}>
                                    {getStatusText(selectedPayment.status)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="font-medium">Создан:</span>
                                  <span className="text-sm">{selectedPayment.created_moscow_str || new Date(selectedPayment.created_at).toLocaleString()} МСК</span>
                                </div>
                                {selectedPayment.expires_at && (
                                  <div className="flex justify-between">
                                    <span className="font-medium">Истекает:</span>
                                    <span className="text-sm">{selectedPayment.expires_moscow_str || new Date(selectedPayment.expires_at).toLocaleString()} МСК</span>
                                  </div>
                                )}
                                {selectedPayment.expected_amount_usdt && (
                                  <div className="flex justify-between">
                                    <span className="font-medium">Ожидается USDT:</span>
                                    <span>{selectedPayment.expected_amount_usdt}</span>
                                  </div>
                                )}
                                {selectedPayment.generated_address && (
                                  <div className="space-y-1">
                                    <span className="font-medium">Адрес для оплаты:</span>
                                    <div className="text-xs font-mono bg-muted p-2 rounded break-all">
                                      {selectedPayment.generated_address}
                                    </div>
                                  </div>
                                )}
                                {selectedPayment.transaction_hash && (
                                  <div className="space-y-1">
                                    <span className="font-medium">Хеш транзакции:</span>
                                    <div className="text-xs font-mono bg-muted p-2 rounded break-all">
                                      {selectedPayment.transaction_hash}
                                    </div>
                                  </div>
                                )}
                                {selectedPayment.bonus_applied && (
                                  <div className="text-sm text-green-600 font-medium">
                                    ✓ Бонус применен
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <p>Загрузка информации о платеже...</p>
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                ))}
                
                {/* Кнопка "Показать все" */}
                {hasMorePayments && !showAllPayments && (
                  <div className="text-center pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAllPayments(true)}
                    >
                      Показать все платежи ({myPayments.length})
                    </Button>
                  </div>
                )}
                
                {/* Кнопка "Показать меньше" */}
                {showAllPayments && hasMorePayments && (
                  <div className="text-center pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAllPayments(false)}
                    >
                      Показать только последние 10
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
